/**
 * Figure S5's "★ Shared with this portfolio · one card per owner".
 *
 * Who is in this portfolio and how they came to be — never what anyone pays
 * for. One card per owner: when the portfolio invited them, when they
 * accepted, and what they have chosen to share since. The card closes with
 * the rule the whole screen turns on: being in a portfolio never shows the
 * portfolio any work.
 *
 * Replaces the v1 "Sponsorship & seats" panel, which was licensing wearing a
 * sharing hat.
 */
import type { EntityGroup } from './entityGroups'
import {
  SHARING_IS_THEIRS_TO_GIVE,
  acceptingGrantsNothingLine,
  entitySharing,
  invitedAcceptedLine,
  sharesWithYouLine,
} from './sharing'
import type { SharingEntity } from './sharing'

export interface SharedWithPortfolioProps {
  group: EntityGroup
  /** The portfolio reading this screen. */
  portfolioName: string
  /** Open the C1 sharing drawer for one owner. */
  onManage?: (entityId: string) => void
  /** Folders this owner has hidden from the portfolio, when the caller knows. */
  hiddenFolderCountByEntity?: Record<string, number>
}

export default function SharedWithPortfolio({
  group,
  portfolioName,
  onManage,
  hiddenFolderCountByEntity,
}: SharedWithPortfolioProps) {
  const members = group.members as SharingEntity[]
  // Each owner's own granted count, not the group's — the card is per owner.
  const grantedByOwner = new Map<string, number>()
  for (const project of group.projects) {
    grantedByOwner.set(project.entity_id, (grantedByOwner.get(project.entity_id) ?? 0) + 1)
  }

  return (
    <div data-part="SharedWithPortfolio" className="light-card p-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-[15px] font-bold text-iflow-900">★ Shared with this portfolio</h3>
        <span className="text-[12px] text-gray-500">· one card per owner</span>
      </div>

      {members.length === 0 ? (
        <p className="mt-2 text-[12.5px] text-gray-600">
          Nothing is shared with this portfolio by this entity yet.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {members.map((member) => {
            const sharing = entitySharing(member, grantedByOwner.get(member.entity_id) ?? 0)
            return (
              <div
                key={member.entity_id}
                data-testid="shared-with-portfolio-card"
                className="rounded-lg border border-iflow-900/10 bg-white p-3"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <b className="text-[12.5px] text-iflow-900">{member.entity_name}</b>
                  <button
                    type="button"
                    onClick={() => onManage?.(member.entity_id)}
                    className="shrink-0 text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline"
                  >
                    Manage →
                  </button>
                </div>
                <div className="mt-1 text-[11.5px] leading-relaxed text-gray-600">
                  {invitedAcceptedLine(sharing, portfolioName)}
                </div>
                <div className="text-[11.5px] leading-relaxed text-gray-600">
                  <span className="text-gray-500">Shares with you: </span>
                  <b className="text-iflow-900">
                    {sharesWithYouLine(sharing, hiddenFolderCountByEntity?.[member.entity_id])}
                  </b>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500">
        {acceptingGrantsNothingLine()}
      </p>
      <p className="mt-2 rounded-lg border-l-4 border-iflow-500 bg-iflow-50/70 px-3 py-2 text-[12px] text-iflow-900">
        {SHARING_IS_THEIRS_TO_GIVE}
      </p>
    </div>
  )
}
